import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  CircularProgress as MuiCircularProgress
} from "@mui/material";
import { FC, Fragment, ReactNode } from "react";
import { ButtonSize, TertiaryButtonTheme } from "../button.constants";
import classnames from "classnames";
import styles from "./tertiary-button.styles.module.scss";

type TertiaryButtonProps = {
  children: ReactNode;
  theme?: `${TertiaryButtonTheme}`;
  size?: `${ButtonSize}`;
  ariaLabel?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  href?: string;
  startIcon?: MuiButtonProps["startIcon"];
  endIcon?: MuiButtonProps["endIcon"];
  onClick?: MuiButtonProps["onClick"];
  rootClassName?: string;
  additionalProps?: Partial<MuiButtonProps>;
};

export const TertiaryButtonComponent: FC<TertiaryButtonProps> = (
  {
    children,
    theme = TertiaryButtonTheme.LIGHT,
    size = ButtonSize.DEFAULT,
    ariaLabel,
    isDisabled = false,
    isLoading = false,
    href,
    startIcon,
    endIcon,
    onClick,
    rootClassName = "",
    additionalProps = {}
  }
) => {
  const handleClick: MuiButtonProps["onClick"] = (event) => {
    if (isLoading) {
      return;
    }
    onClick?.(event);
  };

  return (
    <MuiButton
      {...additionalProps}
      aria-label={ariaLabel}
      classes={{
        root: classnames(
          styles.button,
          styles.buttonRoot,
          {
            [TertiaryButtonTheme.LIGHT]: styles.buttonThemeLight,
            [TertiaryButtonTheme.LIGHT_DESTRUCTIVE]: styles.buttonThemeLightDest,
            [TertiaryButtonTheme.DARK]: styles.buttonThemeDark,
            [TertiaryButtonTheme.DARK_DESTRUCTIVE]: styles.buttonThemeDarkDest,
            [TertiaryButtonTheme.GREY]: styles.buttonThemeGrey,
          }[theme],
          size === ButtonSize.SMALL && styles.sizeSmall,
          rootClassName,
        ),
        startIcon: classnames(
          startIcon && styles.buttonStartIcon,
          isLoading && styles.invisible
        ),
        endIcon: classnames(
          endIcon && styles.buttonEndIcon,
          isLoading && styles.invisible
        ),
        disabled: styles.buttonDisabled,
      }}
      TouchRippleProps={{
        classes: {
          root: styles.rippleRoot,
          ripple: styles.ripple,
        }
      }}
      disabled={isDisabled}
      disableElevation={true}
      disableFocusRipple={true}
      href={href}
      onClick={handleClick}
      startIcon={startIcon}
      endIcon={endIcon}
    >
      <Fragment>
        <span
          className={classnames(
            styles.text,
            isLoading && styles.invisible,
            size === ButtonSize.SMALL && styles.sizeSmall,
          )}
        >
          {children}
        </span>
        {isLoading &&
          <span className={styles.loaderContainer}>
            <MuiCircularProgress
              classes={{
                root: classnames(
                  styles.loader,
                  {
                    [TertiaryButtonTheme.LIGHT]: styles.loaderThemeLight,
                    [TertiaryButtonTheme.LIGHT_DESTRUCTIVE]: styles.loaderThemeLightDest,
                    [TertiaryButtonTheme.DARK]: styles.loaderThemeDark,
                    [TertiaryButtonTheme.DARK_DESTRUCTIVE]: styles.loaderThemeDarkDest,
                    [TertiaryButtonTheme.GREY]: styles.loaderThemeGrey
                  }[theme]
                )
              }}
              size={24}
              variant={"indeterminate"}
            />
          </span>
        }
      </Fragment>
    </MuiButton>
  );
};
